import { memo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import { camelCase, startCase } from 'lodash';
import PropTypes from 'prop-types';

import TransactionDetailModal from '#client/components/TransactionDetailModal';
import DirectionTypeIcon from '#client/components/TransactionDirectionTypeIcon';
import { DATE_FORMAT } from '#client/constants/date';
import { MONEY_COLOR, TRANSACTION_DIRECTION } from '#client/constants/transaction';
import useModalContext from '#client/hooks/useModalContext';

import Money from './Money';

function TransactionRow({
  id,
  createdAt,
  summary,
  direction,
  amount,
  type,
  reason,
  balanceAmount,
  description,
  cardId,
  from,
  to,
  // Merchant Details
  paymentMethod,
  digitalWallet,
  cardPresent,
  recurring,
  verificationMethod,
  merchantName,
  merchantLocation,
  merchantCategory,
  // ATM Details
  atmName,
  atmLocation,
  surcharge,
  // FX Details
  currencyConversion,
  counterpartyIdFromTransaction
}) {
  const { openModal } = useModalContext();

  const trackButtonPressed = useButtonPressedCallback({
    elementName: 'link_account_modal',
    elementText: 'transactions_expanded',
    appPageName: 'active_banking_dashboard'
  });
  const clickHandler = () => {
    // Tracking Details: Tracking added to click on transactions to see details
    trackButtonPressed();

    openModal({
      component: TransactionDetailModal,
      amount,
      createdAt,
      type,
      summary,
      balanceAmount,
      id,
      cardId,
      from,
      to,
      direction,
      description,
      reason: reason ? startCase(camelCase(reason)) : null,
      // Merchant Details
      paymentMethod,
      digitalWallet,
      cardPresent,
      recurring,
      verificationMethod,
      merchantName,
      merchantLocation,
      merchantCategory,
      // ATM Details
      atmName,
      atmLocation,
      surcharge,
      // FX Details
      currencyConversion,
      counterpartyIdFromTransaction
    });
  };

  const moneyColor =
    direction === TRANSACTION_DIRECTION.CREDIT ? MONEY_COLOR.GREEN : MONEY_COLOR.PRIMARY;

  return (
    <Stack
      onClick={clickHandler}
      role="listitem"
      sx={{
        cursor: 'pointer',
        padding: '15px 10px',
        '&:hover': {
          boxShadow: '0px 4px 16px 2px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        pr={0}
        width="100%"
      >
        <Stack
          alignItems="center"
          direction="row"
          spacing={{ xs: '16px', md: '32px' }}
        >
          <Stack>
            <DirectionTypeIcon direction={direction} />
          </Stack>
          <Stack
            alignItems="start"
            direction="column"
            spacing="16px"
          >
            <Text
              size="md"
              weight="medium"
            >
              {summary}
            </Text>
            <Text
              color="primary"
              size="xs"
              whiteSpace="nowrap"
            >
              {dayjs(createdAt).format(DATE_FORMAT.PRIMARY)}
            </Text>
          </Stack>
        </Stack>
        <Money
          amount={amount}
          color={moneyColor}
          direction={direction}
        />
      </Stack>
    </Stack>
  );
}

TransactionRow.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  direction: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  balanceAmount: PropTypes.string,
  description: PropTypes.string,
  reason: PropTypes.string,
  cardId: PropTypes.string,
  from: PropTypes.shape({
    accountHolder: PropTypes.string,
    rootingNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string,
    accountNumber: PropTypes.string
  }),
  to: PropTypes.shape({
    accountHolder: PropTypes.string,
    rootingNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string,
    accountNumber: PropTypes.string
  }),
  // Merchant Detail
  paymentMethod: PropTypes.string,
  digitalWallet: PropTypes.string,
  cardPresent: PropTypes.bool,
  recurring: PropTypes.bool,
  verificationMethod: PropTypes.string,
  merchantName: PropTypes.string,
  merchantLocation: PropTypes.string,
  merchantCategory: PropTypes.string,
  // ATM Details
  atmName: PropTypes.string,
  atmLocation: PropTypes.string,
  surcharge: PropTypes.string,
  // FX Details
  currencyConversion: PropTypes.shape({
    originalCurrency: PropTypes.string,
    amountInOriginalCurrency: PropTypes.number,
    fxRate: PropTypes.string
  }),
  counterpartyIdFromTransaction: PropTypes.string
};

export default memo(TransactionRow);
